import { isDnd } from '@nexo/types';
import { Avatar, Popover, cn } from '@nexo/ui';
import { useMutation } from '@tanstack/react-query';
import {
  BellOff,
  BellRing,
  LogOut,
  MessageSquareWarning,
  Smile,
  Sparkles,
  UserRound,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { formatTime } from '../../lib/format';
import { useAuth, useMe } from '../auth/AuthProvider';
import { FeedbackDialog } from '../feedback/FeedbackDialog';
import { unseenEntries, useChangelog } from '../news/useChangelog';
import { StatusDialog } from './StatusDialog';
import { UserBadges } from './UserBadges';

const minutesFromNow = (minutes: number) => new Date(Date.now() + minutes * 60_000).toISOString();
const tomorrowAt9 = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(9, 0, 0, 0);
  return date.toISOString();
};

/** Avatar menu in the top bar: status, do not disturb, profile and sign out. */
export function UserMenu() {
  const me = useMe();
  const { setUser, logout } = useAuth();
  const [statusOpen, setStatusOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const dndOn = isDnd(me);
  const { t } = useTranslation();
  const hasNews = unseenEntries(useChangelog().data ?? [], me).length > 0;

  const dnd = useMutation({
    mutationFn: (until: string | null) => api.users.setDnd(until),
    onSuccess: setUser,
  });

  return (
    <>
      <Popover
        className="w-72 p-1.5 text-text"
        trigger={({ toggle }) => (
          <button
            type="button"
            onClick={toggle}
            title={t('Tu cuenta y estado')}
            className="relative ml-1 rounded-full ring-2 ring-white/15 hover:ring-white/40"
          >
            <Avatar id={me.id} name={me.displayName} src={me.avatarUrl} size="sm" />
            {dndOn && (
              <span className="absolute -right-1 -bottom-1 flex size-4 items-center justify-center rounded-full bg-danger text-white ring-2 ring-ink">
                <BellOff className="size-2.5" />
              </span>
            )}
          </button>
        )}
      >
        {({ close }) => (
          <div className="flex flex-col text-sm">
            <div className="flex items-center gap-3 px-2.5 py-2">
              <Avatar id={me.id} name={me.displayName} src={me.avatarUrl} />
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 truncate font-medium">
                  {me.displayName} <UserBadges user={me} />
                </p>
                <p className="truncate text-xs text-muted">
                  {me.status ? `${me.status.emoji} ${me.status.text}` : me.email}
                </p>
              </div>
            </div>
            <div className="my-1 border-t border-border" />
            <Item
              icon={<Smile />}
              onClick={() => {
                close();
                setStatusOpen(true);
              }}
            >
              {me.status ? t('Cambiar estado') : t('Poner un estado')}
            </Item>
            {dndOn ? (
              <Item icon={<BellRing />} onClick={() => dnd.mutate(null)}>
                {t('Desactivar «No molestar»')}
                {me.dndUntil && (
                  <span className="ml-auto text-xs text-muted">
                    {t('hasta {{time}}', { time: formatTime(me.dndUntil) })}
                  </span>
                )}
              </Item>
            ) : (
              <>
                <p className="px-2.5 pt-2 pb-1 text-[11px] font-semibold tracking-wider text-subtle uppercase">
                  {t('No molestar')}
                </p>
                <div className="grid grid-cols-2 gap-1 px-1 pb-1">
                  {(
                    [
                      ['30 min', () => minutesFromNow(30)],
                      ['1 hora', () => minutesFromNow(60)],
                      ['2 horas', () => minutesFromNow(120)],
                      ['Hasta mañana', tomorrowAt9],
                    ] as const
                  ).map(([label, until]) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => dnd.mutate(until())}
                      className="rounded-lg border border-border px-2 py-1.5 text-xs hover:bg-surface-2"
                    >
                      {t(label)}
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="my-1 border-t border-border" />
            <Link
              to="/app/profile"
              onClick={close}
              className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 hover:bg-surface-2 [&_svg]:size-4 [&_svg]:text-muted"
            >
              <UserRound /> {t('Perfil y preferencias')}
            </Link>
            <Link
              to="/app/news"
              onClick={close}
              className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 hover:bg-surface-2 [&_svg]:size-4 [&_svg]:text-muted"
            >
              <Sparkles /> {t('Novedades')}
              {hasNews && (
                <span className="ml-auto rounded-full bg-highlight px-1.5 text-[11px] font-semibold text-highlight-fg">
                  {t('Nuevo')}
                </span>
              )}
            </Link>
            <Item
              icon={<MessageSquareWarning />}
              onClick={() => {
                close();
                setFeedbackOpen(true);
              }}
            >
              {t('Reportar un error o sugerir una mejora')}
            </Item>
            <Item icon={<LogOut />} onClick={() => void logout()}>
              {t('Cerrar sesión')}
            </Item>
          </div>
        )}
      </Popover>
      <StatusDialog open={statusOpen} onClose={() => setStatusOpen(false)} />
      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </>
  );
}

function Item({
  icon,
  onClick,
  children,
}: {
  icon: ReactNode;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-surface-2 [&_svg]:size-4 [&_svg]:text-muted',
      )}
    >
      {icon}
      {children}
    </button>
  );
}
